import { useState } from "react";
import { useAuth } from "@/lib/AuthContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { User, Phone, Pencil, Save, X, Info, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

function fmtPhone(p) {
  const d = (p || "").replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("1")) return `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return p || "";
}

export default function ContactInfoCard() {
  const { user, checkAuth } = useAuth();
  const [editing, setEditing] = useState(false);
  const [first, setFirst] = useState(user?.first_name || "");
  const [last, setLast] = useState(user?.last_name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const save = async () => {
    setSaving(true);
    try {
      await API.put("/auth/me", {
        first_name: first.trim(),
        last_name: last.trim(),
        phone: phone.replace(/\D/g, ""),
      });
      await checkAuth();
      toast({ title: "Contact info saved" });
      setEditing(false);
    } catch (err) {
      toast({ title: "Couldn't save", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setFirst(user?.first_name || "");
    setLast(user?.last_name || "");
    setPhone(user?.phone || "");
    setEditing(false);
  };

  const hasGaps = !user?.first_name || !user?.last_name || !user?.phone;
  const isProvider = user?.role === "admin";

  return (
    <div className="bg-card border border-border rounded-xl p-4 space-y-3" data-testid="contact-info-card">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <User className="w-4 h-4 text-primary" /> Contact info
        </h3>
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            className="text-xs text-primary font-medium hover:underline inline-flex items-center gap-1"
            data-testid="contact-info-edit-button"
          >
            <Pencil className="w-3 h-3" /> Edit
          </button>
        )}
      </div>

      {!editing && hasGaps && !isProvider && (
        <div className="flex gap-2 bg-amber-50 border border-amber-200 rounded-lg p-2.5" data-testid="contact-info-gaps-banner">
          <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800 leading-snug">
            We're missing some details. Add them so your provider can text you back through Spruce.
          </p>
        </div>
      )}

      {editing ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">First name</Label>
              <Input value={first} onChange={(e) => setFirst(e.target.value)} className="h-9 mt-1" data-testid="contact-first-name-input" />
            </div>
            <div>
              <Label className="text-xs">Last name</Label>
              <Input value={last} onChange={(e) => setLast(e.target.value)} className="h-9 mt-1" data-testid="contact-last-name-input" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Mobile phone</Label>
            <Input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 123-4567"
              className="h-9 mt-1"
              data-testid="contact-phone-input"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Used by your care team to text you securely through Spruce.
            </p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={saving} className="rounded-full gap-1.5" data-testid="contact-info-save-button">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save
            </Button>
            <Button size="sm" variant="outline" onClick={cancel} disabled={saving} className="rounded-full gap-1.5" data-testid="contact-info-cancel-button">
              <X className="w-3.5 h-3.5" /> Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2 text-sm">
          <Row label="Name" value={[user?.first_name, user?.last_name].filter(Boolean).join(" ") || (user?.full_name || "—")} />
          <Row label="Email" value={user?.email || "—"} />
          <Row
            label={<span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> Phone</span>}
            value={fmtPhone(user?.phone) || "—"}
          />
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 border-b border-border last:border-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium truncate">{value}</span>
    </div>
  );
}
